'use client';

import { Typography, Link, Stack, useMediaQuery } from '@mui/material';
import styled from '@emotion/styled';
import { color, spacing } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import theme from '@/theme/theme';

const MapLink = styled(Link)`
    display: inline-block;
    text-decoration: underline;
    color: ${color.brand[50]} !important;
    width: fit-content;
`;

const ContactInfo = styled(Stack)`
    gap: ${spacing[2]};
`;


const EmailLink = styled(Link)`
    display: inline-block;
    text-decoration: underline;
    color: ${color.brand[50]} !important;
    width: fit-content;
`;

const WebsiteLink = styled(Link)`
    display: inline-block;
    text-decoration: underline;
    color: ${color.brand[50]} !important;
    width: fit-content;
`;

const PhoneLink = styled(Link)`
    display: inline-block;
    text-decoration: none;
    color: inherit !important;
    width: fit-content;
    &:hover {
        text-decoration: underline;
    }
`;

type ContactLocationProps = {
    id?: string;
    name: string;
    address: string[] | string;
    mapUrl: string;
    phone?: string;
    fax?: string;
    email?: string;
    website?: string;
    manager?: string;
    company?: string;
    isOfficeContact?: boolean;
    isAuthenticated?: boolean;
};

export default function ContactLocation({
    id,
    name,
    address,
    mapUrl,
    phone,
    fax,
    email,
    website,
    manager,
    company,
    isOfficeContact = false,
    isAuthenticated = false
}: ContactLocationProps) {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = useTranslations('ContactsPage');

    return (
        <Stack id={id} direction="column" gap={spacing[8]}>
            <Typography variant="label5semibold" component="h4" sx={{ whiteSpace: 'pre-line' }}>
                {name}
            </Typography>

            {company && (
                <Typography component="p" variant="label5medium">
                    {company}
                </Typography>
            )}

            <Typography variant="label5regular">
                {Array.isArray(address) ? address.map((line, index) => (
                    // Static, non-reordered list built from derived data — index keys are stable here.
                    // react-doctor-disable-next-line no-array-index-as-key
                    <span key={index}>
                        {line}
                        {index < address.length - 1 && <br />}
                    </span>
                )) : address}
            </Typography>

            <MapLink href={mapUrl} target="_blank" rel="noopener noreferrer">
                <Typography variant="label5regular" color={color.brand[50]}>
                    {t('map')}
                </Typography>
            </MapLink>

            <ContactInfo>
                {manager && (
                    <Typography component="p" variant="label5regular">
                        {manager}
                    </Typography>
                )}

                {phone && (
                    <Typography component="p" variant="label5regular">
                        {t('tel')}: {' '}
                        <PhoneLink href={`tel:${phone}`}>
                            {phone}
                        </PhoneLink>
                    </Typography>
                )}

                {fax && (
                    <Typography component="p" variant="label5regular">
                        {t('fax')}: {' '}
                        <PhoneLink href={`tel:${fax}`}>
                            {fax}
                        </PhoneLink>
                    </Typography>
                )}

                {email && (!isOfficeContact || isAuthenticated) && (
                    <Typography component="p" variant="label5regular">
                        {!isMobile && <span>{t('email')}: {' '}</span>}
                        <EmailLink href={`mailto:${email}`}>
                            {email}
                        </EmailLink>
                    </Typography>
                )}

                {website && (
                    <Typography component="p" variant="label5regular">
                        {!isMobile && <span>{t('website')}: {' '}</span>}
                        <WebsiteLink href={`https://${website}`} target="_blank" rel="noopener noreferrer">
                            {website}
                        </WebsiteLink>
                    </Typography>
                )}
            </ContactInfo>
        </Stack>
    );
}
